import type {
  Customer,
  Invoice,
  InvoiceItem,
  InvoiceStatus,
  Supplier,
} from "@shared/types";
import type { InvoiceTotals } from "@shared/totals";

export class ApiError extends Error {
  status: number;
  code: string | null;
  constructor(message: string, status: number, code: string | null = null) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  return (await send(path, init)).json() as Promise<T>;
}

async function send(path: string, init?: RequestInit): Promise<Response> {
  const r = await fetch(path, { credentials: "same-origin", ...init });
  if (r.status === 401) {
    if (
      typeof window !== "undefined" &&
      window.location.pathname !== "/login"
    ) {
      window.location.replace("/login");
    }
    throw new ApiError("unauthorized", 401, "unauthorized");
  }
  if (!r.ok) {
    const text = await r.text();
    let code: string | null = null;
    try {
      const parsed = text ? (JSON.parse(text) as { error?: unknown }) : null;
      if (parsed && typeof parsed.error === "string") code = parsed.error;
    } catch {
      // body wasn't JSON; keep code null
    }
    throw new ApiError(code ?? text ?? `http ${r.status}`, r.status, code);
  }
  return r;
}

export const apiGet = <T>(path: string) => request<T>(path);
export const apiGetText = async (path: string) => (await send(path)).text();
export const apiPost = <T>(path: string, body?: unknown) =>
  request<T>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
export const apiPatch = <T>(path: string, body: unknown) =>
  request<T>(path, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
export const apiDelete = <T>(path: string) =>
  request<T>(path, { method: "DELETE" });

// ---------- Domain helpers ----------

export interface InvoiceListRow extends Invoice {
  customer: { id: number; name: string | null; ico: string | null };
  totals: InvoiceTotals;
}

export interface InvoiceDetail {
  invoice: Invoice;
  items: InvoiceItem[];
  customer: Customer;
  supplier: Supplier;
  totals: InvoiceTotals;
}

export interface StatsSummary {
  year: number;
  monthPrefix: string;
  monthGross: number;
  ytdNet: number;
  ytdVat: number;
  ytdGross: number;
  ytdHours: number;
  counts: { draft: number; issued: number };
  topCustomers: { id: number; name: string | null; gross: number }[];
}

export interface ProjectionSummary {
  year: number;
  thresholdNet: number;
  monthsElapsed: number;
  months: {
    month: number;
    net: number;
    vat: number;
    gross: number;
    count: number;
  }[];
  totals: { net: number; vat: number; gross: number; count: number };
  averages: {
    perInvoiceNet: number;
    perInvoiceGross: number;
    perMonthNet: number;
    perMonthGross: number;
  };
  projection: {
    projectedNet: number;
    projectedGross: number;
    paceMonthlyNet: number;
    overThreshold: boolean;
  };
  headroom: { remainingNet: number; utilizationPct: number };
}

export interface ItemPayload {
  name: string;
  quantity: number;
  unit?: string;
  unit_price_net: number;
  vat_rate?: number;
  sort_order?: number;
}

export interface InvoiceCreatePayload {
  customer_id: number;
  number?: string | null;
  issue_date?: string;
  delivery_date?: string;
  due_date?: string;
  variable_symbol?: string | null;
  constant_symbol?: string | null;
  specific_symbol?: string | null;
  payment_method?: string;
  note?: string | null;
  status?: InvoiceStatus;
  items: ItemPayload[];
}

export type InvoicePatchPayload = Partial<
  Omit<InvoiceCreatePayload, "items">
> & { items?: ItemPayload[] };

export const Customers = {
  list: () => apiGet<{ customers: Customer[] }>("/api/customers"),
  create: (body: Partial<Customer> & { name: string }) =>
    apiPost<{ customer: Customer }>("/api/customers", body),
  update: (id: number, body: Partial<Customer>) =>
    apiPatch<{ customer: Customer }>(`/api/customers/${id}`, body),
};

export const Invoices = {
  list: (q: {
    year?: number;
    status?: InvoiceStatus;
    customerId?: number;
    limit?: number;
  } = {}) => {
    const params = new URLSearchParams();
    if (q.year !== undefined) params.set("year", String(q.year));
    if (q.status) params.set("status", q.status);
    if (q.customerId !== undefined)
      params.set("customer_id", String(q.customerId));
    if (q.limit !== undefined) params.set("limit", String(q.limit));
    const qs = params.toString();
    return apiGet<{ invoices: InvoiceListRow[] }>(
      `/api/invoices${qs ? `?${qs}` : ""}`
    );
  },
  get: (idOrSlug: string | number) =>
    apiGet<InvoiceDetail>(`/api/invoices/${idOrSlug}`),
  nextNumber: (date?: string) => {
    const qs = new URLSearchParams();
    if (date) qs.set("date", date);
    const q = qs.toString();
    return apiGet<{ number: string; variable_symbol: string }>(
      `/api/invoices/next-number${q ? `?${q}` : ""}`
    );
  },
  create: (body: InvoiceCreatePayload) =>
    apiPost<InvoiceDetail>("/api/invoices", body),
  update: (idOrSlug: string | number, body: InvoicePatchPayload) =>
    apiPatch<{
      invoice: Invoice;
      items: InvoiceItem[];
      totals: InvoiceTotals;
    }>(`/api/invoices/${idOrSlug}`, body),
  remove: (idOrSlug: string | number) =>
    apiDelete<{ ok: true }>(`/api/invoices/${idOrSlug}`),
  duplicate: (idOrSlug: string | number) =>
    apiPost<InvoiceDetail>(`/api/invoices/${idOrSlug}/duplicate`),
  exportEinvoice: (idOrSlug: string | number, xml: string) =>
    apiPost<{ invoice: Invoice }>(`/api/invoices/${idOrSlug}/einvoice`, {
      xml,
    }),
  getEinvoice: (idOrSlug: string | number) =>
    apiGetText(`/api/invoices/${idOrSlug}/einvoice`),
};

export const Stats = {
  summary: () => apiGet<StatsSummary>("/api/stats/summary"),
  projection: (year?: number) =>
    apiGet<ProjectionSummary>(
      `/api/stats/projection${year ? `?year=${year}` : ""}`
    ),
};

export const Settings = {
  getSupplier: () => apiGet<{ supplier: Supplier }>("/api/settings/supplier"),
  updateSupplier: (body: Partial<Supplier>) =>
    apiPatch<{ supplier: Supplier }>("/api/settings/supplier", body),
};
